import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CaseStudiesPage() {
  const testimonials = [
    {
      quote: 'Transformative AI implementation that exceeded expectations',
      text: 'FluxByte delivered a custom machine learning solution that reduced our processing time by 60% and improved accuracy significantly. Their team understood our challenges and built exactly what we needed.',
      name: 'Sarah Mitchell',
      title: 'CTO, TechCorp UK',
      avatar: 'https://public.readdy.ai/ai/img_res/b5697bdecd705727198f7c78f9c29b4d.jpg',
      company: 'TechCorp UK',
      industry: 'Technology',
      results: [
        '60% reduction in processing time',
        '95% accuracy improvement',
        '£500K annual cost savings',
        'Deployed in 3 months'
      ]
    },
    {
      quote: 'Scalable AI that grows with our business',
      text: 'The intelligent automation system FluxByte built has transformed our operations. It handles complex workflows seamlessly and continues to improve over time. Outstanding technical expertise and support.',
      name: 'James Chen',
      title: 'Operations Director, DataFlow Ltd',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20headshot%20confident%20technology%20director%20modern%20office%20setting&width=80&height=80&seq=avatar-large2&orientation=squarish',
      company: 'DataFlow Ltd',
      industry: 'Logistics',
      results: [
        '10x faster processing',
        '40% cost reduction',
        '99.8% accuracy rate',
        'Zero downtime deployment'
      ]
    },
    {
      quote: 'Predictive insights that drive real business value',
      text: 'Working with FluxByte has given us a competitive edge. Their predictive analytics platform provides insights we never had before, enabling smarter decisions and better outcomes across the organization.',
      name: 'Emily Roberts',
      title: 'Head of Analytics, Innovate Group',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20headshot%20confident%20analytics%20executive%20modern%20office%20setting&width=80&height=80&seq=avatar-large3&orientation=squarish',
      company: 'Innovate Group',
      industry: 'Finance',
      results: [
        '25% revenue increase',
        '95% forecast accuracy',
        '30% better decision speed',
        'ROI achieved in 6 months'
      ]
    },
    {
      quote: 'Game-changing automation for our industry',
      text: 'FluxByte\'s AI automation solution revolutionized our quality control process. What used to take hours now happens in minutes, with better accuracy than manual inspection. Truly impressive work.',
      name: 'Michael Thompson',
      title: 'Quality Director, PrecisionMfg',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20headshot%20confident%20quality%20director%20modern%20office%20setting&width=80&height=80&seq=avatar-large4&orientation=squarish',
      company: 'PrecisionMfg',
      industry: 'Manufacturing',
      results: [
        '99.9% defect detection',
        '80% time savings',
        '£1M annual savings',
        'Zero false positives'
      ]
    },
    {
      quote: 'Exceptional AI expertise and partnership',
      text: 'From initial consultation to deployment, FluxByte demonstrated deep AI expertise and genuine partnership. They didn\'t just build what we asked for—they helped us understand what we truly needed.',
      name: 'Lisa Anderson',
      title: 'Chief Digital Officer, RetailNext',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20headshot%20confident%20digital%20officer%20modern%20office%20setting&width=80&height=80&seq=avatar-large5&orientation=squarish',
      company: 'RetailNext',
      industry: 'Retail',
      results: [
        '35% customer satisfaction boost',
        '50% faster response times',
        '20% revenue growth',
        'Seamless integration'
      ]
    },
    {
      quote: 'AI that actually delivers on its promises',
      text: 'We\'ve worked with several AI vendors, but FluxByte stands out. Their solutions are practical, scalable, and deliver real ROI. The ongoing support and optimization have been exceptional.',
      name: 'David Kumar',
      title: 'VP of Technology, HealthTech Solutions',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20headshot%20confident%20technology%20VP%20modern%20office%20setting&width=80&height=80&seq=avatar-large6&orientation=squarish',
      company: 'HealthTech Solutions',
      industry: 'Healthcare',
      results: [
        '70% faster diagnosis',
        '98% accuracy rate',
        'HIPAA compliant',
        'Scaled to 50K users'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-orange-400/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-orange-700 text-xs uppercase tracking-wider font-semibold mb-4">
              Client Success Stories
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Real Results from<br />Real Partnerships
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              See how businesses across industries have transformed their operations and achieved 
              measurable success with FluxByte's AI solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{testimonial.name}</h3>
                    <div className="text-gray-600 text-sm">{testimonial.title}</div>
                    <div className="text-gray-500 text-xs mt-1">{testimonial.company}</div>
                    <div className="inline-block mt-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                      {testimonial.industry}
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-500 text-lg"></i>
                  ))}
                </div>

                {/* Quote */}
                <h4 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  "{testimonial.quote}"
                </h4>

                {/* Testimonial Text */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {testimonial.text}
                </p>

                {/* Results */}
                <div className="border-t border-gray-200 pt-6">
                  <h5 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                    Key Results
                  </h5>
                  <div className="grid grid-cols-2 gap-3">
                    {testimonial.results.map((result, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-sm mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700 text-xs">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Proven Track Record
            </h2>
            <p className="text-gray-600 text-lg">
              Numbers that demonstrate our commitment to client success
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { number: '50+', label: 'Successful Projects', icon: 'ri-checkbox-circle-line' },
              { number: '95%', label: 'Client Satisfaction', icon: 'ri-emotion-happy-line' },
              { number: '40%', label: 'Average Cost Reduction', icon: 'ri-line-chart-line' },
              { number: '10+', label: 'Industries Served', icon: 'ri-building-line' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-orange-100 rounded-full">
                  <i className={`${stat.icon} text-3xl text-orange-600`}></i>
                </div>
                <div className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join the growing list of businesses achieving exceptional results with FluxByte AI solutions.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-600 rounded-full font-semibold text-base hover:shadow-2xl transition-all cursor-pointer whitespace-nowrap"
          >
            Start Your Project →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
